import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";

interface NotificationPreferences {
  emailQueryCompletions: boolean;
  weeklyReports: boolean;
  securityAlerts: boolean;
  productUpdates: boolean;
  marketingCommunications: boolean;
}

export const NotificationSettings: React.FC = () => {
  const [notificationPreferences, setNotificationPreferences] =
    useState<NotificationPreferences>({
      emailQueryCompletions: true,
      weeklyReports: true,
      securityAlerts: false,
      productUpdates: false,
      marketingCommunications: false,
    });
  const [savingNotifications, setSavingNotifications] = useState(false);

  const saveNotificationPreferences = async () => {
    setSavingNotifications(true);
    try {
      // TODO: Replace with actual API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Notification preferences saved successfully");
    } catch (error: any) {
      console.error("Failed to save notification preferences:", error);
      toast.error("Failed to save notification preferences");
    } finally {
      setSavingNotifications(false);
    }
  };

  const updateNotificationPreference = (
    key: keyof NotificationPreferences,
    value: boolean
  ) => {
    setNotificationPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const notificationOptions = [
    {
      key: "emailQueryCompletions" as keyof NotificationPreferences,
      label: "Email notifications for query completions",
      description: "Get notified when your queries finish processing",
    },
    {
      key: "weeklyReports" as keyof NotificationPreferences,
      label: "Weekly usage reports",
      description: "Receive weekly summaries of your activity",
    },
    {
      key: "securityAlerts" as keyof NotificationPreferences,
      label: "Security alerts",
      description: "Important security updates and alerts",
    },
    {
      key: "productUpdates" as keyof NotificationPreferences,
      label: "Product updates and announcements",
      description: "New features and product improvements",
    },
    {
      key: "marketingCommunications" as keyof NotificationPreferences,
      label: "Marketing communications",
      description: "Optional promotional emails and newsletters",
    },
  ];

  return (
    <div className="min-h-full">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 lg:py-16">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Notification Settings
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">
            Choose what updates and notifications you receive
          </p>
        </div>

        {/* Notification Preferences */}
        <div className="space-y-6">
          <div className="space-y-4">
            {notificationOptions.map((item) => (
              <Card key={item.key} className="p-4 sm:p-6">
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label
                      htmlFor={item.key}
                      className="text-sm sm:text-base font-medium leading-none cursor-pointer"
                    >
                      {item.label}
                    </Label>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                  <Switch
                    id={item.key}
                    checked={notificationPreferences[item.key]}
                    onCheckedChange={(value) =>
                      updateNotificationPreference(item.key, value)
                    }
                  />
                </div>
              </Card>
            ))}
          </div>

          <div className="flex justify-end pt-4">
            <Button
              onClick={saveNotificationPreferences}
              disabled={savingNotifications}
              className="w-full sm:w-auto"
            >
              {savingNotifications ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Preferences"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
